import { GEO_BOUNDARIES } from '@/constants/endpoints';
import { MAP_SCALES } from '@/constants/geo';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useEffect, useRef, useState } from 'react';
import MapGl, { Layer, Source } from 'react-map-gl';
import { num } from '../format';
import { COLORS } from '../theme';
import MapHoverTooltip from './MapHoverTooltip';
import MapLegend from './MapLegend';
import WidgetSkeleton from './WidgetSkeleton';
import { buildFillPaint } from './geoFillLayer';
import { MARKET_FOCUSED_ZOOM, computeDensityFocusedViewState } from './mapViewport';

const VITE_MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
// Same default camera as the legacy Mapbox maps (constants/geo.js
// INITIAL_VIEW_STATE), minus its width/height fields — sizing here comes
// from the container Box, and react-map-gl's ViewState type doesn't have them.
const INITIAL_VIEW_STATE = { latitude: 38, longitude: -97, zoom: 3, bearing: 0, pitch: 0 };

/**
 * State map — ported from chartjs-chart-geo to a real Mapbox GL choropleth
 * (flank#5184), the same library DmaChoropleth/CityChoropleth/ZipChoropleth
 * already use. See DmaChoropleth's own comment for the full rationale:
 * chartjs-plugin-zoom doesn't support chartjs-chart-geo's projection scale,
 * so there was no way to add zoom/pan to the old bubble-free choropleth
 * without hand-rolling it; Mapbox GL has it built in.
 */
interface StateFeature {
  type: 'Feature';
  properties: { stateCode?: string; name?: string; value?: number | null };
  geometry: unknown;
}

// Beeswax sometimes returns 2-letter codes; canonical GEO_STATE sometimes
// appends "(State)"/"(Province)"/"(Region)" — carried over from the old
// UsChoropleth, which needed the same cleanup to match TopoJSON names.
const CODE_TO_NAME: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};
const NAME_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(CODE_TO_NAME).map(([code, name]) => [name, code]),
);

/**
 * Canonical GEO_STATE value -> the boundary's own 2-letter `stateCode`
 * (ribeye-api's getGeoJsonPart STATE features carry `{name, stateCode}` —
 * see GeographyController.spec.ts). Same normalization the old UsChoropleth
 * needed (a raw 2-letter code, or a full name sometimes canonical-suffixed
 * "(State)"/"(Province)"/"(Region)"), just resolved to a code instead of a
 * name since the boundary this now joins against exposes one directly.
 */
export const codeForStateValue = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.length === 2) return trimmed.toUpperCase();
  const name = trimmed.replace(/\s*\((?:State|Province|Region)\)$/i, '').trim();
  return NAME_TO_CODE[name] ?? '';
};

/**
 * Per-state value, summed rather than overwritten — a state can arrive as
 * both a bare code and a canonical-suffixed full name across rows, and both
 * resolve to the same code (same pattern as ZipChoropleth's sumValuesByZip).
 */
export const sumValuesByState = (rows: ReportRow[], labelKey: string, valueKey: string): Map<string, number> => {
  const valueByCode = new Map<string, number>();
  for (const row of rows) {
    const raw = row[labelKey];
    if (typeof raw !== 'string') continue;
    const code = codeForStateValue(raw);
    const value = Number(row[valueKey] ?? 0);
    if (code && Number.isFinite(value) && value > 0) valueByCode.set(code, (valueByCode.get(code) ?? 0) + value);
  }
  return valueByCode;
};

/**
 * Joins boundary features to their report value by `stateCode`. `null` (not
 * 0) for a state with no delivery, so "no delivery" draws as no-data grey
 * rather than the lightest step of the colour scale — same convention
 * DmaChoropleth/CityChoropleth/ZipChoropleth use.
 */
export const buildStateFeatures = (boundaries: StateFeature[], valueByCode: Map<string, number>): StateFeature[] =>
  boundaries.map(f => {
    const code = String(f.properties?.stateCode ?? '')
      .trim()
      .toUpperCase();
    return { ...f, properties: { ...f.properties, value: valueByCode.get(code) ?? null } };
  });

// search_term values ribeye-api's /geo/boundaries STATE scale matches
// against (getGeoJsonPart.ts: `.whereIn('search_term', geoIds)`). Unlike DMA,
// STATE has no "empty geoIds = whole basemap" special case — confirmed from
// GeoBoundariesValidator's own comment ("may be empty — at DMA scale that
// means the full basemap; every other scale returns nothing") — so the full
// 50-state + DC name list has to be requested explicitly or the fetch comes
// back with zero features.
const STATE_SEARCH_TERMS = Object.values(CODE_TO_NAME);

interface IProps {
  rows: ReportRow[];
  labelKey?: string;
  valueKey?: string;
  metricLabel?: string;
  formatValue?: (v: number) => string;
  /**
   * Chart height in px, excluding the legend beneath it. Custom Dashboards packs
   * this into a shorter widget card, where the 360 default overflowed and clipped
   * the southern states and the Alaska/Hawaii insets (carried over from
   * UsChoropleth, which this replaces).
   */
  chartHeight?: number;
}

let cachedBoundaries: StateFeature[] | null = null;

const StateChoropleth = ({
  rows,
  labelKey = 'geo_state',
  valueKey = 'impressions',
  metricLabel = 'Impressions',
  formatValue = num,
  chartHeight = 360,
}: IProps) => {
  const [boundaries, setBoundaries] = useState<StateFeature[] | null>(cachedBoundaries);
  const [failed, setFailed] = useState(false);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const [hover, setHover] = useState<{ x: number; y: number; label: string; value: number } | null>(null);
  // "Follow legacy", same as DmaChoropleth — zoom to the densest state on load
  // instead of the fixed default view. Only runs once per mount so it doesn't
  // fight the user's own pan/zoom afterward. Uses MARKET_FOCUSED_ZOOM, not the
  // tighter City/ZIP FOCUSED_ZOOM: a state polygon is at least as large as a
  // DMA media market (many states contain a dozen+ DMAs), so the same wide
  // framing DmaChoropleth chose applies here too — the tighter zoom would
  // fill the viewport with one solid state shape and no surrounding context.
  const hasFocusedRef = useRef(false);

  useEffect(() => {
    if (cachedBoundaries) {
      setBoundaries(cachedBoundaries);
      return;
    }
    const controller = new AbortController();
    // All 50 states + DC every time (STATE has no empty-geoIds basemap
    // shortcut, see STATE_SEARCH_TERMS above), so a state is placeable
    // whether or not it delivered in the current window. Same ~51 shapes
    // every time, so it's fetched once and cached at module scope.
    axios
      .post(GEO_BOUNDARIES, { mapScale: MAP_SCALES.STATE, geoIds: STATE_SEARCH_TERMS }, { signal: controller.signal })
      .then(({ data }) => {
        cachedBoundaries = (data?.features ?? []) as StateFeature[];
        setBoundaries(cachedBoundaries);
      })
      .catch(error => {
        if (!axios.isCancel(error)) setFailed(true);
      });
    return () => controller.abort();
  }, []);

  const valueByCode = sumValuesByState(rows, labelKey, valueKey);
  const features = buildStateFeatures(boundaries ?? [], valueByCode);

  // Code review, DmaChoropleth's own note (#0000): the ref flip is a side
  // effect, so it has to run in an effect rather than during render — a
  // discarded render (StrictMode double-invoke, an interrupted concurrent
  // render) could otherwise flip the flag without its setViewState ever
  // committing, silently eating the one-shot auto-focus. Declared before the
  // early returns below (Rules of Hooks).
  useEffect(() => {
    if (!hasFocusedRef.current && features.length > 0) {
      hasFocusedRef.current = true;
      setViewState(computeDensityFocusedViewState(features, INITIAL_VIEW_STATE, MARKET_FOCUSED_ZOOM));
    }
  }, [features]);

  if (failed) {
    return (
      <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
        Couldn't load the state boundaries. The ranked list below still reflects the data.
      </Typography>
    );
  }
  if (!boundaries) return <WidgetSkeleton height={chartHeight} />;

  const scaleMax = Math.max(0, ...Array.from(valueByCode.values()));
  const geojson = { type: 'FeatureCollection' as const, features };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ height: chartHeight, width: '100%', position: 'relative' }}>
        <MapGl
          {...viewState}
          style={{ width: '100%', height: '100%' }}
          onMove={e => setViewState(e.viewState)}
          mapStyle='mapbox://styles/mapbox/light-v9'
          mapboxAccessToken={VITE_MAPBOX_TOKEN}
          interactiveLayerIds={['data']}
          onMouseMove={e => {
            const feature = e.features?.[0] as StateFeature | undefined;
            const value = feature?.properties?.value;
            if (!feature || value == null) {
              setHover(null);
              return;
            }
            setHover({
              x: e.point.x,
              y: e.point.y,
              label: feature.properties?.name ?? '',
              value,
            });
          }}
          onMouseLeave={() => setHover(null)}
        >
          <Source type='geojson' data={geojson}>
            <Layer id='data' type='fill' paint={buildFillPaint(features) as any} />
          </Source>
        </MapGl>
        {hover && (
          <MapHoverTooltip
            x={hover.x}
            y={hover.y}
            label={hover.label}
            metricLabel={metricLabel}
            value={formatValue(hover.value)}
          />
        )}
      </Box>
      <MapLegend max={scaleMax} />
    </Box>
  );
};

export default StateChoropleth;
